import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { deleteClient } from "../components/clients/api";
import { clientQuery, clientVisitLogQuery, clientVisitsQuery } from "../components/clients/queries";
import { ClientIdentity } from "../components/clients/ClientIdentity";
import { ClientTimeline } from "../components/clients/ClientTimeline";
import { CLIENT_TABS } from "../components/clients/clientTabs";
import { ClientStatsCards } from "../components/clients/ClientStatsCards";
import { InvoiceModal } from "../components/invoices/InvoiceModal";
import styles from "../components/clients/ClientProfile.module.css";
import { ApiError } from "../lib/api";
import { queryClient } from "../lib/queryClient";
import { useCan } from "../hooks/useCan";
import { ArrowLeftIcon, MoreIcon } from "../icons";

export const Route = createFileRoute("/_auth/clients/$id")({
  staticData: { titleKey: "nav.clients", breadcrumbKey: "breadcrumb.clientsProfile" },
  // The open timeline tab lives in the URL so a tab can be opened in a new
  // browser tab (right-click, Cmd/Ctrl-click) and survives a reload.
  validateSearch: z.object({ tab: z.enum(CLIENT_TABS).optional() }),
  // Start the profile's queries (and the default Activity tab's) as soon as
  // navigation begins (or, with defaultPreload "intent", on hovering a client
  // link) instead of after the route chunk has loaded and rendered. Not
  // awaited: errors, a 404 included, stay with the component's own inline
  // states, and the page shows its loading state as it always has.
  loader: ({ params }) => {
    void queryClient.prefetchQuery(clientQuery(params.id));
    void queryClient.prefetchQuery(clientVisitsQuery(params.id));
    void queryClient.prefetchInfiniteQuery(clientVisitLogQuery(params.id));
  },
  component: ClientProfile,
});

function ClientProfile() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canDelete = useCan("delete_client");
  const canInvoice = useCan("create_invoice");
  const [menuOpen, setMenuOpen] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const { data: client, isLoading, error, refetch } = useQuery(clientQuery(id));

  const archiveM = useMutation({
    mutationFn: () => deleteClient(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["clients"] });
      void navigate({ to: "/clients" });
    },
  });

  if (error instanceof ApiError && error.status === 404) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <h2 className="h-2">{t("profile.notFound.title")}</h2>
        <p className="muted">{t("profile.notFound.body")}</p>
        <Link to="/clients" className="btn btn--ghost" style={{ marginTop: 16 }}>
          {t("profile.backToClients")}
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: "center", padding: 64 }}>
        <h2 className="h-2">{t("profile.loadError.title")}</h2>
        <p className="muted">{t("profile.loadError.body")}</p>
        <div style={{ marginTop: 16, display: "flex", gap: 8, justifyContent: "center" }}>
          <button className="btn btn--ghost" onClick={() => void refetch()}>
            {t("profile.retry")}
          </button>
          <Link to="/clients" className="btn btn--ghost">
            {t("profile.backToClients")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 24, gap: 16 }}>
        <Link to="/clients" className="btn btn--ghost btn--mini">
          <ArrowLeftIcon size={14} /> {t("profile.backToClients")}
        </Link>
        <Link to="/clients/$id/insights" params={{ id }} className="btn btn--ghost btn--mini">
          {t("profile.insights.link")}
        </Link>
        <div style={{ flex: 1 }} />
        {canInvoice && client && (
          <button className="btn btn--ghost btn--mini" onClick={() => setInvoiceOpen(true)}>
            {t("invoice.action")}
          </button>
        )}
        {canDelete && client && (
          <div style={{ position: "relative" }}>
            <button className="btn btn--ghost btn--mini" onClick={() => setMenuOpen((v) => !v)}>
              <MoreIcon size={16} />
            </button>
            {menuOpen && (
              <div
                style={{
                  position: "absolute",
                  right: 0,
                  top: "100%",
                  marginTop: 8,
                  background: "var(--bg-elev)",
                  border: "1px solid var(--line)",
                  borderRadius: "var(--radius)",
                  padding: 6,
                  minWidth: 160,
                  zIndex: 10,
                  boxShadow: "var(--shadow)",
                }}
                onClick={() => setMenuOpen(false)}
              >
                <button
                  className="btn btn--ghost btn--mini"
                  style={{ width: "100%", justifyContent: "flex-start" }}
                  onClick={() => {
                    if (confirm(t("profile.archiveConfirm"))) {
                      archiveM.mutate();
                    }
                  }}
                >
                  {t("profile.archive")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className={styles.grid}>
        <div className={styles.identity}>
          {isLoading || !client ? (
            <div className="muted">{t("profile.loading")}</div>
          ) : (
            <ClientIdentity client={client} />
          )}
        </div>
        {!isLoading && client && (
          <div className={styles.timeline}>
            <ClientTimeline clientId={client.id} />
          </div>
        )}
        {!isLoading && client && (
          <div className={styles.stats}>
            <ClientStatsCards client={client} />
          </div>
        )}
      </div>

      {invoiceOpen && client && (
        <InvoiceModal client={client} onClose={() => setInvoiceOpen(false)} />
      )}
    </div>
  );
}
